// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthChange } from '../utils/firebase';
import { callApi } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // TEMP DIAGNOSTIC: the blank screen after cold launch was traced to
    // ProtectedRoute rendering nothing while `loading` here stays true —
    // fixed by showing a spinner instead, but that only makes a *slow*
    // resolution visible, not a *stuck* one. This logs whether/when
    // onAuthStateChanged actually fires at all, so a genuinely hung
    // Firebase init (vs. just a slow one) shows up clearly in the Xcode
    // console instead of silence.
    console.log('[auth] AuthProvider mounted, waiting for onAuthStateChanged...');
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      console.log('[auth] onAuthStateChanged fired, user=', firebaseUser?.uid ?? 'null');
      if (firebaseUser) {
        // Build a clean user object
        const userData = {
          uid:         firebaseUser.uid,
          email:       firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL:    firebaseUser.photoURL,
          provider:    firebaseUser.providerData[0]?.providerId || 'unknown',
        };

        setUser(userData);

        // Notify server: upsert user in DB + send login email
        try {
          await callApi('/api/user-login', {
            method: 'POST',
            body: JSON.stringify(userData),
          });
        } catch (err) {
          console.warn('Login notification failed (non-fatal):', err);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
